import { css } from "@emotion/react";
import styled from "@emotion/styled";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";

import { fontMerriweather, fontRoboto, theme, ZIndexLayer } from "shared/lib/styles";

import BackwardIcon from "./backward-icon.svg";
import BlackVoiceIcon from "./black-voice-icon.svg";
import ChevronDownIcon from "./chevron-down-icon.svg";
import CollapseIcon from "./collapse-icon.svg";
import ExpandIcon from "./expand-icon.svg";
import ForwardIcon from "./forward-icon.svg";
import GrayVoiceIcon from "./gray-voice-icon.svg";
import PauseIcon from "./pause-icon.svg";
import PlayIcon from "./play-icon.svg";
import RestartIcon from "./restart-icon.svg";
import TickIcon from "./tick-icon.svg";
import UKFlagIcon from "./UK-flag-icon.svg";
import USFlagIcon from "./US-flag-icon.svg";
import VoicemailIcon from "./voicemail-icon.svg";

type ContainerProps = {
  $isAudioLoaded: boolean;
};

export const Container = styled.div<ContainerProps>`
  display: ${props => (props.$isAudioLoaded ? "flex" : "none")};
`;

export const CollapsedPlayerContainer = styled.div`
  position: fixed;
  inset-block-end: 0;
  inset-inline: 0;
  z-index: ${ZIndexLayer.Drawer - 1};

  display: flex;
  justify-content: space-between;
  align-items: center;

  inline-size: calc(100% - 32px);
  max-inline-size: 420px;
  margin-inline: auto;
  padding-block: 12px;
  padding-inline: 16px;

  background-color: #ffffff;

  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.125);

  border-start-start-radius: 8px;
  border-start-end-radius: 8px;

  ${theme.breakpoints.up("tablet")} {
    inline-size: 288px;
    max-inline-size: 288px;
    margin-inline-end: 16px;
  }
`;

export const CollapsedPlayerLeftContainer = styled.div`
  display: flex;
  align-items: center;

  column-gap: 8px;
`;

export const CollapsedPlayerRightContainer = styled.div``;

export const CollapsedPlayerPlayIconContainer = styled.div`
  inline-size: 30px;
  block-size: 30px;

  cursor: pointer;

  svg {
    inline-size: 30px;
    block-size: 30px;
  }
`;

export const RemainingTimeContainer = styled.div`
  color: #777777;
  font-weight: 500;
  font-size: 14px;
  font-family: ${fontRoboto};
  font-style: normal;
  line-height: normal;
  text-align: center;
`;

export const ExpandIconContainer = styled.div`
  cursor: pointer;
`;

type ExpandedPlayerContainerProps = {
  $isShowingHeader: boolean;
};

export const ExpandedPlayerContainer = styled.div<ExpandedPlayerContainerProps>`
  position: fixed;
  inset-block-end: 16px;
  inset-inline: 0;
  z-index: ${ZIndexLayer.Drawer - 1};

  display: flex;
  flex-direction: column;

  inline-size: calc(100% - 32px);
  max-inline-size: 420px;
  margin-inline: auto;
  padding: ${props => (props.$isShowingHeader ? "8px" : "0")};

  border-radius: 8px;

  background: ${props =>
    props.$isShowingHeader
      ? `linear-gradient(
      0deg,
      rgba(69, 133, 255, 0.2) 0%,
      rgba(69, 133, 255, 0.2) 100%
    ),
    #fff`
      : `#fff`};

  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.125);

  ${theme.breakpoints.up("tablet")} {
    inset-block-end: 32px;

    inline-size: 288px;
    max-inline-size: 288px;
    margin-inline-end: 32px;
  }
`;

type HeaderContainerProps = {
  $isShowingHeader: boolean;
};

export const HeaderContainer = styled.div<HeaderContainerProps>`
  display: ${props => (props.$isShowingHeader ? "flex" : "none")};

  padding-block-end: 8px;
  padding-inline: 4px;

  column-gap: 8px;
`;

export const ThumbnailImage = styled.img``;

export const ThumbnailContainer = styled.div`
  flex-shrink: 0;

  inline-size: 44px;
  block-size: 44px;

  ${ThumbnailImage} {
    inline-size: 100%;
    block-size: 100%;

    border-radius: 4px;

    object-fit: cover;
  }
`;

export const Title = styled.div`
  display: -webkit-box;

  block-size: calc(18.2px * 2);
  margin-block: 4px;
  padding-inline: 0;

  color: #001246;
  font-weight: 400;
  font-size: 14px;
  font-family: ${fontMerriweather};
  font-style: normal;
  line-height: 18.2px;
  text-overflow: ellipsis;

  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const CollapseIconContainer = styled.div`
  block-size: fit-content;

  cursor: pointer;
`;

export const StyledCollapseIcon = styled(CollapseIcon)``;

export const StyledExpandIcon = styled(ExpandIcon)``;

export const PlayerContainer = styled.div`
  padding-block: 8px;

  border-radius: 6px;

  background-color: #ffffff;
`;

type VoiceListContainerProps = {
  $shouldShowVoiceList: boolean;
};

export const VoiceListContainer = styled.div<VoiceListContainerProps>`
  display: grid;
  grid-template-rows: ${props => (props.$shouldShowVoiceList ? "1fr" : "0fr")};

  transition: grid-template-rows 0.15s ease-out;

  & > div {
    overflow: hidden;
  }
`;

export const VoiceList = styled.div`
  background-color: #f6f6f6;
`;

export const VoiceGrid = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-block: 12px;
  padding-inline: 16px;
  border-block-end: 1px solid #d7d7d7;

  cursor: pointer;
`;

export const LeftContainer = styled.div`
  display: flex;

  column-gap: 8px;
`;

export const FlagIconContainer = styled.div``;

export const VoiceTextContainer = styled.div`
  display: flex;
  flex-direction: column;

  text-transform: capitalize;
`;

export const VoiceName = styled.div`
  color: #000000;
  font-weight: 600;
  font-size: 16px;
  font-family: ${fontRoboto};
  font-style: normal;
  line-height: 140%;
`;

export const VoiceInfoRow = styled.div`
  display: flex;
  flex-direction: row;

  color: #666666;
  font-weight: 400;
  font-size: 13px;
  font-family: ${fontRoboto};
  font-style: normal;
  line-height: 140%;
`;

export const StyledUKFlagIcon = styled(UKFlagIcon)``;

export const StyledUSFlagIcon = styled(USFlagIcon)``;

export const RightContainer = styled.div``;

export const StyledTickIcon = styled(TickIcon)``;

export const GrayCircle = styled.div`
  inline-size: 24px;
  block-size: 24px;

  border-radius: 50%;

  background-color: #d7d7d7;
`;

type PlaybackRateRadioButtonsContainerProps = {
  $shouldShowPlaybackRateRadioButtons: boolean;
};

export const PlaybackRateRadioButtonsContainer = styled.div<PlaybackRateRadioButtonsContainerProps>`
  display: grid;
  grid-template-rows: ${props => (props.$shouldShowPlaybackRateRadioButtons ? "1fr" : "0fr")};

  transition: grid-template-rows 0.15s ease-out;

  & > div {
    overflow: hidden;
  }
`;

export const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding-block: 12px;
  padding-inline: 16px;

  border-block-end: 1px solid #d7d7d7;
`;

export const StyledVoicemailIcon = styled(VoicemailIcon)``;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

export const TitleText = styled.div`
  color: #000000;
  font-weight: 600;
  font-size: 16px;
  font-family: ${fontRoboto};
  font-style: normal;
  line-height: 20.522px;
`;

export const StyledChevronDownIcon = styled(ChevronDownIcon)`
  cursor: pointer;
`;

export const StyledFormControl = styled(FormControl)`
  inline-size: 100%;
`;

export const StyledRadioGroup = styled(RadioGroup)``;

export const StyledFormControlLabel = styled(FormControlLabel)`
  justify-content: space-between;

  margin: 0;
  padding-block: 12px;
  padding-inline: 16px;
  border-block-end: 1px solid #d7d7d7;

  background: #f6f6f6;

  .MuiFormControlLabel-label {
    color: #000000;
    font-weight: 600;
    font-size: 16px;
    font-family: ${fontRoboto};
    font-style: normal;
    line-height: 140%;
  }
`;

export const SeekBarContainer = styled.div`
  display: flex;
  align-items: center;

  padding: 8px;
`;

const seekBarThumbStyle = css`
  inline-size: 11px;
  block-size: 11px;
  margin-block-start: -4px;

  border-radius: 50%;

  background-color: #4585ff;

  cursor: pointer;

  appearance: none;
`;

const getSeekBarTrackStyle = ($played: number) => css`
  block-size: 2px;

  background: ${`linear-gradient(to right, #4585ff 0 ${
    $played * 100
  }%, #999999 ${$played * 100}% 100%)`};
`;

type SeekBarProps = {
  $played: number;
};

export const SeekBar = styled.input<SeekBarProps>`
  flex-grow: 1;

  margin: 0;
  padding: 8px;

  cursor: pointer;

  appearance: none;

  &::-webkit-slider-thumb {
    ${seekBarThumbStyle}
  }

  &::-moz-range-thumb {
    ${seekBarThumbStyle}
  }

  &::-webkit-slider-runnable-track {
    ${props => getSeekBarTrackStyle(props.$played)}
  }

  &::-moz-range-track {
    ${props => getSeekBarTrackStyle(props.$played)}
  }
`;

export const Time = styled.time`
  color: #999999;
  font-weight: 500;
  font-size: 13px;
  font-style: normal;
  line-height: normal;
  text-align: center;
  text-transform: uppercase;
`;

export const ButtonGroupContainer = styled.div`
  display: flex;
  justify-content: space-evenly;
  align-items: center;

  inline-size: 100%;

  * {
    display: flex;
    flex-grow: 1;
    justify-content: center;

    cursor: pointer;
  }
`;

export const CenterButtonGroupContainer = styled.div`
  display: flex;
  align-items: center;

  column-gap: 18px;

  * {
    flex-grow: 0;
  }
`;

export const StyledBlackVoiceIcon = styled(BlackVoiceIcon)``;

export const StyledGrayVoiceIcon = styled(GrayVoiceIcon)``;

export const StyledBackwardIcon = styled(BackwardIcon)``;

export const StyledForwardIcon = styled(ForwardIcon)``;

export const PlayIconContainer = styled.div``;

export const StyledPauseIcon = styled(PauseIcon)``;

export const StyledPlayIcon = styled(PlayIcon)``;

export const StyledRestartIcon = styled(RestartIcon)``;

export const PlaybackRateButton = styled.div`
  color: #999999;
  font-weight: 500;
  font-size: 14px;
  font-family: ${fontRoboto};
  line-height: 36px;
  text-align: center;

  cursor: pointer;
`;
